/**
 * Contrat de données partagé par toute l'application.
 * Les modules de vue, les stores et la couche de persistance s'y réfèrent :
 * il est la seule source de vérité sur la forme d'un souvenir.
 */

/** Moment de la journée auquel un souvenir se rattache. */
export type MomentId = "ceremonie" | "cocktail" | "diner" | "soiree";

/** Provenance des fichiers entrants, qui décide du geste de complément proposé. */
export type FileSource = "library" | "camera";

/** Nature du média. */
export type MediaKind = "image" | "video";

/** Provenance : contenu de démonstration livré avec l'album, ou dépôt d'un invité. */
export type MediaOrigin = "demo" | "guest";

/** Onglet de filtrage de la galerie. */
export type FilterId = "all" | MomentId | "video";

/** Réglage de cadrage d'une vignette découpée dans une planche contact. */
export interface MediaCrop {
  /** Valeur CSS `background-position`. */
  position: string;
  /** Valeur CSS `background-size`. */
  size: string;
}

/** Gabarit de la carte dans la mosaïque. */
export type MediaSpan = "tall" | "wide" | "hero";

/** Un souvenir tel que l'affiche la galerie. */
export interface MediaItem {
  id: string;
  kind: MediaKind;
  origin: MediaOrigin;
  author: string;
  /** Moment de la journée. Absent quand l'invité n'en a pas choisi. */
  moment?: MomentId | undefined;
  caption: string;
  /** Horodatage de prise de vue, en millisecondes epoch. */
  createdAt: number;
  liked: boolean;
  /** Visuel affiché dans la grille et dans la visionneuse. */
  previewUrl: string;
  /** Cadrage à appliquer au visuel, pour les vignettes issues d'une planche. */
  crop?: MediaCrop;
  /** Source lisible en plein écran. Présent uniquement pour les vidéos. */
  playbackUrl?: string;
  /** Durée lisible affichée sur la vignette vidéo, ex. « 0:14 ». */
  durationLabel?: string;
  /** Gabarit dans la mosaïque. Absent = gabarit standard. */
  span?: MediaSpan;
}

/** Enregistrement persisté d'un souvenir déposé par un invité. */
export interface StoredMedia {
  id: string;
  kind: MediaKind;
  author: string;
  moment?: MomentId | undefined;
  caption: string;
  createdAt: number;
  liked: boolean;
  /** Fichier d'origine, conservé intact. */
  blob: Blob;
}

/** Enregistrement persisté d'un message du livre d'or vocal. */
export interface VoiceNote {
  id: string;
  createdAt: number;
  /** Durée de l'enregistrement en millisecondes. */
  durationMs: number;
  author: string;
  /** Moment de la journée, comme pour un souvenir photo ou vidéo. Facultatif. */
  moment?: MomentId | undefined;
  blob: Blob;
}

/** Fichier retenu dans la feuille d'ajout, avant validation définitive. */
export interface PendingFile {
  id: string;
  file: File;
  kind: MediaKind;
  /** Object URL d'aperçu, révoqué au retrait du fichier. */
  previewUrl: string;
}

/** Motif de rejet d'un fichier par la validation. */
export type RejectionReason = "type" | "size" | "duplicate" | "batch-limit";

/** Résultat de la validation d'une sélection de fichiers. */
export interface ValidationResult {
  accepted: File[];
  rejected: Array<{ file: File; reason: RejectionReason }>;
}

/** Libellés des moments, dans l'ordre d'affichage. */
export const MOMENT_LABELS: Readonly<Record<MomentId, string>> = Object.freeze({
  ceremonie: "Cérémonie",
  cocktail: "Cocktail",
  diner: "Dîner",
  soiree: "Soirée",
});

/** Onglets de la barre de filtres, dans l'ordre d'affichage. */
export const FILTERS: ReadonlyArray<{ id: FilterId; label: string }> = Object.freeze([
  { id: "all", label: "Tous" },
  { id: "ceremonie", label: "Cérémonie" },
  { id: "cocktail", label: "Cocktail" },
  { id: "diner", label: "Dîner" },
  { id: "soiree", label: "Soirée" },
  { id: "video", label: "Vidéos" },
]);
